/** Shared Google Places → form field mapping (Create List Property, Profile, etc.) */

export interface LocationPickerPayload {
  address: string
  latitude: string
  longitude: string
  city: string
  state: string
  pinCode: string
  microMarketLocality: string
  roadName: string
  buildingName: string
  googleMapsLink: string
}

export interface ProfileAddressFields {
  address: string
  city: string
  state: string
  pinCode: string
}

export const PLACES_AUTOCOMPLETE_FIELDS = [
  "address_components",
  "geometry",
  "formatted_address",
  "name",
  "url",
  "types",
] as const

export function getAddressComponent(
  place: google.maps.places.PlaceResult | { address_components?: google.maps.GeocoderAddressComponent[] },
  type: string
): string {
  const c = place.address_components?.find((x) => x.types.includes(type))
  return c?.long_name ?? ""
}

export function resolveStateForDropdown(geoState: string, options: { value: string; label: string }[]): string {
  if (!geoState || !options?.length) return geoState || ""
  const s = geoState.trim()
  const exact = options.find((o) => o.value === s || o.label === s)
  if (exact) return exact.value
  const lower = s.toLowerCase()
  const match = options.find((o) => o.value.toLowerCase() === lower || o.label.toLowerCase() === lower)
  if (match) return match.value
  const contains = options.find((o) => o.label.toLowerCase().includes(lower) || o.value.toLowerCase().includes(lower))
  if (contains) return contains.value
  return s
}

function extractBuildingName(place: google.maps.places.PlaceResult): string {
  const name = place.name?.trim() || ""
  const formatted = place.formatted_address?.trim() || ""
  const types = place.types || []
  const isNamedPlace = types.some((t) =>
    ["establishment", "point_of_interest", "premise", "subpremise"].includes(t)
  )
  if (name && name !== formatted && (isNamedPlace || !formatted.includes(name))) {
    return name
  }
  return (
    getAddressComponent(place, "premise") ||
    getAddressComponent(place, "subpremise") ||
    getAddressComponent(place, "establishment") ||
    ""
  )
}

export function getDisplayAddressFromPlace(place: google.maps.places.PlaceResult): string {
  const formatted = place.formatted_address?.trim() || ""
  const name = place.name?.trim() || ""
  if (name && formatted) {
    if (formatted.toLowerCase().includes(name.toLowerCase())) return formatted
    return `${name}, ${formatted}`
  }
  return formatted || name
}

export function extractRoadName(
  place: google.maps.places.PlaceResult | { address_components?: google.maps.GeocoderAddressComponent[] }
): string {
  const streetNumber = getAddressComponent(place, "street_number")
  const route = getAddressComponent(place, "route")
  if (route) {
    return streetNumber ? `${streetNumber}, ${route}` : route
  }
  for (const type of [
    "intersection",
    "sublocality_level_3",
    "sublocality_level_2",
    "neighborhood",
    "premise",
  ]) {
    const value = getAddressComponent(place, type)
    if (value) return value
  }
  return ""
}

export function buildLocationPayloadFromPlace(
  place: google.maps.places.PlaceResult,
  stateOpts?: { value: string; label: string }[]
): LocationPickerPayload {
  const lat = place.geometry?.location?.lat()
  const lng = place.geometry?.location?.lng()
  const postalCode = getAddressComponent(place, "postal_code")
  const locality = getAddressComponent(place, "locality")
  const stateFromGeo = getAddressComponent(place, "administrative_area_level_1")
  const state = stateOpts?.length ? resolveStateForDropdown(stateFromGeo, stateOpts) : stateFromGeo || ""
  const city = locality || getAddressComponent(place, "administrative_area_level_2") || ""
  const sublocality =
    getAddressComponent(place, "sublocality_level_1") ||
    getAddressComponent(place, "sublocality") ||
    getAddressComponent(place, "neighborhood") ||
    ""
  const roadName = extractRoadName(place)
  const buildingName = extractBuildingName(place)
  const googleMapsLink =
    place.url || (lat != null && lng != null ? `https://www.google.com/maps?q=${lat},${lng}` : "")

  return {
    address: getDisplayAddressFromPlace(place),
    city,
    state,
    pinCode: postalCode,
    microMarketLocality: sublocality,
    latitude: lat != null ? String(lat) : "",
    longitude: lng != null ? String(lng) : "",
    roadName,
    buildingName,
    googleMapsLink,
  }
}

/** Geocoder fallback when Places result has no route (e.g. malls, POIs). */
export function enrichLocationPayloadWithRoad(
  payload: LocationPickerPayload,
  place: google.maps.places.PlaceResult
): Promise<LocationPickerPayload> {
  if (payload.roadName) return Promise.resolve(payload)
  const lat = place.geometry?.location?.lat()
  const lng = place.geometry?.location?.lng()
  if (lat == null || lng == null || !window.google?.maps) return Promise.resolve(payload)

  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder()
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const road = extractRoadName(results[0])
        if (road) {
          resolve({ ...payload, roadName: road })
          return
        }
      }
      resolve(payload)
    })
  })
}

export function locationPayloadToProfileFields(payload: LocationPickerPayload): ProfileAddressFields {
  return {
    address: payload.address,
    city: payload.city,
    state: payload.state,
    pinCode: payload.pinCode,
  }
}
